import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import MenuItem, { type MenuItemProps } from "./MenuItem";

const StyledWrapper = styled.div`
  .DropdownMenuContent {
    animation-duration: 0.6s;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  }
  .DropdownMenuContent[data-side="top"] {
    animation-name: slideUp;
  }
  .DropdownMenuContent[data-side="bottom"] {
    animation-name: slideDown;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MenuVariant = tv({
  slots: {
    base: "bg-surface-default border-border-subdued relative mt-1 flex w-full flex-col items-start justify-start rounded-xl border border-solid shadow-lg",
  },
  variants: { borderless: { true: { base: "border-none" } } },
});

type MenuItemData = Omit<MenuItemProps, "handleValueChange"> & {
  key?: string;
  value?: string;
  category?: string;
};

interface MenuProps {
  data: MenuItemData[];
  divider?: boolean;
  borderless?: boolean;
  content?: React.ReactNode;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  useTriggerWidth?: boolean;
  onPointerDownOutside?: () => void;
  multiSelect?: boolean;
  selectable?: boolean;
  buttons?: React.ReactNode;
  handleValueChange?: (value: string | null) => void;
}

const Menu = ({
  handleValueChange,
  borderless,
  data,
  content,
  divider,
  triggerRef,
  useTriggerWidth,
  onPointerDownOutside,
  multiSelect,
  selectable,
  buttons,
}: MenuProps): JSX.Element => {
  const { t } = useTranslation();
  const { base } = MenuVariant({ borderless });
  const menuRef = React.useRef<HTMLDivElement>(null);

  const groupedData = React.useMemo(() => {
    return data.reduce<Record<string, MenuItemData[]>>((acc, item) => {
      const category = item.category || "";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});
  }, [data]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const currentIndex = Number.parseInt(
      target.getAttribute("data-index") || "0",
      10,
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (menuRef.current) {
          const nextElement = menuRef.current.querySelector(
            `[data-index="${currentIndex + 1}"]`,
          ) as HTMLElement;
          nextElement?.focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (menuRef.current) {
          const prevElement = menuRef.current.querySelector(
            `[data-index="${currentIndex - 1}"]`,
          ) as HTMLElement;
          prevElement?.focus();
        }
        break;
      case "Escape":
        triggerRef?.current?.focus();
        onPointerDownOutside?.();
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        target.click();
        break;
      default:
        break;
    }
  };

  return (
    <DropdownMenu.Portal>
      <StyledWrapper>
        <DropdownMenu.Content
          collisionPadding={10}
          className={cn(base(), "DropdownMenuContent")}
          style={{
            overflow: "auto",
            minWidth: "0",
            width: useTriggerWidth
              ? "var(--radix-dropdown-menu-trigger-width)"
              : undefined,
            maxWidth: "300px !important",
            maxHeight: useTriggerWidth
              ? "calc(var(--radix-dropdown-menu-content-available-height) - 10px)"
              : undefined,
            zIndex: 99999999999999,
          }}
          onPointerDownOutside={onPointerDownOutside}
          ref={menuRef}
          onKeyDown={handleKeyDown}
        >
          {content ||
            Object.entries(groupedData).map(([category, items], groupIndex) => (
              <DropdownMenu.Group
                key={groupIndex}
                className={cn(
                  "category-group flex w-full flex-col gap-1 p-1",
                  !useTriggerWidth && "max-w-[295px]",
                )}
              >
                {category !== "" && (
                  <DropdownMenu.Label className="body-md text-text-subdued mx-2 mt-2">
                    {t(category)}
                  </DropdownMenu.Label>
                )}

                {items.map((item, itemIndex) => {
                  const { key, ...itemProps } = item;
                  const itemKey =
                    key ||
                    item.id ||
                    `${category}-${item.primaryContent}-${itemIndex}`;
                  return (
                    <MenuItem
                      key={itemKey}
                      {...itemProps}
                      index={itemIndex}
                      multiSelect={multiSelect}
                      selectable={selectable}
                      handleValueChange={handleValueChange}
                    />
                  );
                })}
              </DropdownMenu.Group>
            ))}
          {buttons && (
            <div className="sticky bottom-0 flex w-full justify-end gap-1 border-t bg-white pb-2 pr-2 pt-2">
              {buttons}
            </div>
          )}
        </DropdownMenu.Content>
      </StyledWrapper>
    </DropdownMenu.Portal>
  );
};

export default Menu;
